import React, { useState } from "react";
import { createCardToken } from "../services/api";
import { useTranslation } from "../i18n/LanguageContext";
import { AlertCircle } from "lucide-react";

export default function MpCardForm({ amount, onTokenReady, onError }) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const [cardNumber, setCardNumber] = useState("");
  const [expMonth, setExpMonth] = useState("");
  const [expYear, setExpYear] = useState("");
  const [cvv, setCvv] = useState("");
  const [cardholderName, setCardholderName] = useState("");

  const formatCardNumber = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 16);
    return digits.replace(/(.{4})/g, "$1 ").trim();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setFormError(null);

    try {
      const res = await createCardToken({
        card_number: cardNumber.replace(/\s/g, ""),
        expiration_month: expMonth,
        expiration_year: expYear,
        security_code: cvv,
        cardholder_name: cardholderName,
      });

      const data = res.data;
      if (data.token) {
        onTokenReady({
          token: data.token,
          payment_method_id: data.payment_method_id || "",
          issuer_id: data.issuer_id || "",
          installments: 1,
        });
      } else {
        throw new Error("Failed to create card token");
      }
    } catch (err) {
      console.error("Token error:", err);
      const msg = err.response?.data?.error || "Error processing card";
      const details = err.response?.data?.details;
      const fullMsg = details
        ? typeof details === "object"
          ? `${msg}: ${JSON.stringify(details)}`
          : `${msg}: ${details}`
        : msg;
      setFormError(fullMsg);
      if (onError) onError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const fieldClass = "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none";
  const labelClass = "block text-xs font-medium text-gray-600 mb-1";

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {formError && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{formError}</span>
        </div>
      )}
      <div>
        <label className={labelClass}>{t("upgrade.cardNumber")}</label>
        <input
          className={fieldClass}
          value={cardNumber}
          onChange={(e) => { setCardNumber(formatCardNumber(e.target.value)); setFormError(null); }}
          placeholder="0000 0000 0000 0000"
          inputMode="numeric"
          autoComplete="cc-number"
          required
        />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className={labelClass}>{t("upgrade.expiration")}</label>
          <div className="flex gap-1">
            <input
              className={fieldClass}
              value={expMonth}
              onChange={(e) => setExpMonth(e.target.value.replace(/\D/g, "").slice(0, 2))}
              placeholder="MM"
              inputMode="numeric"
              autoComplete="cc-exp-month"
              required
            />
            <input
              className={fieldClass}
              value={expYear}
              onChange={(e) => setExpYear(e.target.value.replace(/\D/g, "").slice(0, 2))}
              placeholder="YY"
              inputMode="numeric"
              autoComplete="cc-exp-year"
              required
            />
          </div>
        </div>
        <div>
          <label className={labelClass}>CVV</label>
          <input
            className={fieldClass}
            value={cvv}
            onChange={(e) => setCvv(e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder="123"
            inputMode="numeric"
            autoComplete="cc-csc"
            type="password"
            required
          />
        </div>
        <div />
      </div>
      <div>
        <label className={labelClass}>{t("upgrade.cardholderName")}</label>
        <input
          className={fieldClass}
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value.toUpperCase())}
          placeholder="JOHN DOE"
          autoComplete="cc-name"
          required
        />
      </div>
      <button
        type="submit"
        disabled={submitting}
        className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm transition mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? (
          <>
            <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            {t("upgrade.processing")}
          </>
        ) : (
          <>
            ${amount?.toFixed(2)} MXN — {t("upgrade.pay")}
          </>
        )}
      </button>
    </form>
  );
}
